import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  projectId: string;
  currentSlug: string | null;
  /** Called after a successful save so the parent can refresh the loaded
   *  script (which carries the slug via the projects JOIN). */
  onSaved: (next: string | null) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * View + edit the parent project's `dashboard_slug`. The slug is the key
 * the client-facing dashboard worker uses to look this project up, so
 * mis-typing it during script creation silently breaks the dashboard
 * link until corrected — which is exactly what this panel fixes.
 *
 * Important UX note: the slug is project-scoped, not script-scoped. One
 * edit here updates every script sharing this project's id. The body
 * copy below the input states that explicitly so the operator doesn't
 * have to remember the data model to use this safely.
 */
export default function DashboardSlugPanel({
  projectId,
  currentSlug,
  onSaved,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentSlug ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function startEdit() {
    setDraft(currentSlug ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
    setDraft(currentSlug ?? '');
  }

  async function handleSave() {
    const trimmed = draft.trim();
    const next: string | null = trimmed === '' ? null : trimmed;
    if (next !== null && !SLUG_RE.test(next)) {
      setError(
        'Slug can only contain lowercase letters, numbers, and hyphens (max 63 chars).',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.setProjectDashboardSlug(projectId, next);
      onSaved(res.dashboard_slug);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function copySlug() {
    if (!currentSlug) return;
    try {
      await navigator.clipboard.writeText(currentSlug);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — copy fallback isn't worth complicating this panel for
    }
  }

  return (
    <section
      data-print-hide
      className="bg-card border border-line rounded-md px-5 py-4"
    >
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>// Dashboard slug</Eyebrow>
        {!editing && (
          <button
            onClick={startEdit}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-muted hover:text-fg transition-colors
            "
          >
            {currentSlug ? 'Edit' : 'Set'}
          </button>
        )}
      </div>

      {editing ? (
        <div>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.toLowerCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') cancelEdit();
            }}
            placeholder="acme-co"
            disabled={saving}
            autoFocus
            className="
              w-full font-mono text-[13px]
              bg-page border border-line rounded px-3 py-2 text-fg
              focus:outline-none focus:border-accent transition-colors
              disabled:opacity-50
            "
          />
          {error && (
            <p className="mt-2 text-accent text-[12px]">{error}</p>
          )}
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Lowercase a–z, 0–9, hyphens. Leave blank to clear.
            <br />
            <strong className="text-muted font-medium">
              Affects every script in this project.
            </strong>
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : currentSlug ? (
        <div>
          <button
            onClick={copySlug}
            title="Click to copy"
            className="
              block w-full text-left font-mono text-[13px] text-fg
              bg-page/60 border border-line rounded px-3 py-2
              hover:border-accent transition-colors
            "
          >
            {copied ? '✓ Copied' : currentSlug}
          </button>
          <p className="mt-2 text-faint text-[11px]">
            Project-wide. Shared with every script in this project.
          </p>
        </div>
      ) : (
        <p className="text-muted text-[13px] leading-relaxed">
          No slug set. The client dashboard webhook will be a silent no-op
          until you add one.
        </p>
      )}
    </section>
  );
}
